import Modal from 'components/common/modal';
import { useModal } from 'hooks/use-modal';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { RadioGroup, RadioGroupItem } from 'components/common/radio-group';
import { Label } from 'components/common/label';
import ButtonPrimary from 'components/common/button/primary';
import { usePostApiV1ClientAdsMutation } from 'service/api/allApis';
import { formatPrice } from 'lib/utils';
import { useSelector } from 'react-redux';
import { Form } from 'components/common/form';
import { useForm } from 'react-hook-form';
import { selectUser } from 'store/features/userSlice';
import { FaMoneyBillAlt } from 'react-icons/fa';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { paths } from 'lib/constants/paths';
import { RenderIf } from 'components/common/render-if';

const AdvertPaymentModal = () => {
    const { isOpen, type, onClose, data: modalData } = useModal();
    const { user } = useSelector(selectUser);
    const [createMutation, { isLoading: isCreateLoading }] = usePostApiV1ClientAdsMutation();
    const navigate = useNavigate();

    const form = useForm({
        defaultValues: {
            planId: '',
            paymentMethod: 'credit-card',
        },
    });
    const paymentMethod = form.watch('paymentMethod');

    const isModalOpen = isOpen && type == 'advertPayment';
    const { createData } = modalData;
    const { t } = useTranslation();

    async function onSubmit(values: { paymentMethod: string }) {
        if (!createData?.formData) return;
        const { paymentMethod } = values;
        const payWithBalance = paymentMethod === 'balance';
        const { formData } = createData;

        formData.set('payWithBalance', payWithBalance);

        createMutation({
            body: formData as any,
        })
            .unwrap()
            .then((res) => {
                if (payWithBalance) {
                    toast.success(t('success.advert-create'));
                    navigate(paths.cabinet.advert);
                } else {
                    const { paymentUrl } = res as { paymentUrl: string };
                    if (paymentUrl) {
                        window.location.href = paymentUrl;
                        return;
                    }
                    throw new Error(t('general.something-went-wrong'));
                }
            })
            .catch((e) => {
                const errorMessage =
                    e?.data?.Detail ??
                    e?.data?.Title ??
                    e?.data?.message ??
                    t('general.something-went-wrong');
                toast.error(errorMessage);
            });
    }

    return (
        <Modal
            isOpen={isModalOpen}
            onClose={onClose}
            modalContainerClassName="flex"
            modalClassName="w-full max-w-[550px] mx-6  bg-white rounded-2xl p-4 md:p-8"
        >
            <Form {...form}>
                <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="flex flex-col justify-center items-center gap-y-7"
                >
                    <div className="flex gap-x-2 items-center">
                        <FaMoneyBillAlt className="w-8 h-8 fill-green-700" />
                        <h3 className="font-bold text-lg md:text-3xl">{t('advert.payment')}</h3>
                    </div>
                    <p className="w-full text-left">{t('advert.payment-description')}</p>
                    <div className="w-full">
                        <h5 className="text-brend uppercase border-y border-brend w-full text-center py-1 md:py-2">
                            {t('advert.select-payment-type')}
                        </h5>
                        <div className="mt-4 md:mt-6">
                            <RadioGroup
                                value={paymentMethod}
                                onValueChange={(value) => {
                                    form.setValue('paymentMethod', value);
                                }}
                            >
                                <div className="flex items-center space-x-6 md:text-lg pb-4 border-b border-brend-20 mb-3">
                                    <RadioGroupItem value="credit-card" id="credit-card" />
                                    <Label htmlFor="credit-card">
                                        {t('cabinet.payment.with-card')}
                                    </Label>
                                </div>
                                <RenderIf condition={!!user}>
                                    <div className="flex items-center space-x-6 md:text-lg pb-4 border-b border-brend-20 mb-3">
                                        <RadioGroupItem value="balance" id="balance" />
                                        <Label htmlFor="balance">
                                            {t('cabinet.payment.with-balance')} (
                                            {formatPrice(user?.balance)} AZN)
                                        </Label>
                                    </div>
                                </RenderIf>
                            </RadioGroup>
                        </div>
                    </div>
                    <ButtonPrimary
                        loading={isCreateLoading}
                        type="submit"
                        className="w-full"
                        text={t('general.pay')}
                    />
                </form>
            </Form>
        </Modal>
    );
};

export default AdvertPaymentModal;
